<template>
    <component
        :is="rootComponent"
        v-bind="rootProps"
        class="
            d-block
            w-100
            h-100
            position-relative
            no-underline-link
        "
    >
        <div
            class="
                d-flex
                w-100
                h-100
                justify-center
                position-relative
            "
            :class="isMobile ? 'flex-column' : 'flex-row'"
        >

            <!-- Media -->
            <div
                class="
                    d-flex
                    flex-column
                "
                :class="[
                    mediaPositionClasses,
                    mediaPadClass,
                    {
                        'order-md-1': modelValue.mediaSide === 'left',
                        'order-md-2': modelValue.mediaSide === 'right'
                    }
                ]"
                :style="isMobile ? 'width: 100%; height: 100%;' : 'width: 50%;'"
            >
                <template
                    v-if="modelValue.altImage && modelValue.image"
                >
                    <FullImage
                        v-if="isMobile"
                        :image-object="modelValue.altImage"
                        :type="isMobile ? 'medium' : 'large'"
                        :style="mediaStyle"
                        scalable
                    />
                    <FullImage
                        v-else
                        :image-object="modelValue.image"
                        :type="isMobile ? 'medium' : 'large'"
                        :style="mediaStyle"
                        scalable
                    />
                </template>
                <template
                    v-else-if="modelValue.image"
                >
                    <FullImage
                        :image-object="modelValue.image"
                        :type="isMobile ? 'medium' : 'large'"
                        :style="mediaStyle"
                        scalable
                    />
                </template>
                <template
                    v-else-if="modelValue.videoSm && isMobile"
                >
                    <BlockTypeVideoStatic
                        :model-value="modelValue.videoSm"
                    />
                </template>
                <template
                    v-else-if="modelValue.videoLg"
                >
                    <BlockTypeVideoStatic
                        :model-value="modelValue.videoLg"
                    />
                </template>
            </div>

            <!-- Text -->
            <div
                class="
                    d-flex
                    flex-column
                "
                :class="[
                    textPositionClasses,
                    {
                        'order-md-2': modelValue.mediaSide === 'left',
                        'order-md-1': modelValue.mediaSide === 'right',
                        'position-absolute absolute-center': isMobile
                    }
                ]"
                :style="isMobile ? 'width: auto;' : 'width: 50%;'"
            >
                <div
                    class="
                        fit-content
                        rounded-lg
                    "
                    :class="[
                        textAlignClass,
                        textThemeClass,
                        textPadClass,
                        textOverlayClass
                    ]"
                >
                    <div
                        v-if="modelValue.headingImage && !isHeadingImageError"
                        class="
                            pa-0
                            ma-0
                        "
                    >
                        <v-img
                            :src="modelValue.headingImage.url"
                            :alt="modelValue.headingImage.title"
                            :aspect-ratio="modelValue.headingImage.aspectRatio"
                            width="auto"
                            :height="`calc(60px * ${modelValue.headingImage.scale})`"
                            class="mx-auto"
                            @error="onHeadingImageError"
                        />
                    </div>
                    <h2
                        v-else
                        class="
                            text-display-medium
                            text-md-display-large
                            font-weight-bold
                            text-uppercase
                            pa-0
                            ma-0
                        "
                        :class="[
                            textStrokeClass,
                            {
                                'text-drop-shadow': modelValue.headingDropShadow
                            }
                        ]"
                    >
                        {{ modelValue.heading }}
                    </h2>
                    <div
                        v-if="modelValue.subHeading"
                        class="
                            mt-2
                            text-headline-small
                            text-md-headline-medium
                        "
                        :class="[
                            textStrokeClass,
                            {
                                'text-drop-shadow': modelValue.subHeadingDropShadow
                            }
                        ]"
                    >
                        {{ modelValue.subHeading }}
                    </div>
                    <div
                        v-if="buttons.length"
                        class="
                            d-flex
                            flex-wrap
                            justify-center
                            mt-4
                        "
                    >
                        <BtnUniversal
                            v-for="(button, index) in buttons"
                            :key="`hero-carousel-item-btn-${index}`"
                            :params="{ content: button.label, path: button.link }"
                            :is-nested-link="isBlockLink"
                            :theme="buttonTheme"
                            color="secondary"
                            :size="isMobile ? 'default' : 'large'"
                            class="ma-2"
                            :class="{
                                'bg-secondary': isBlockLink
                            }"
                            unset-default-label-case
                        >
                            {{ button.label }}
                        </BtnUniversal>
                    </div>
                </div>
            </div>
        </div>
    </component>
</template>

<script lang="ts" setup>
    import type {
        HeroCarouselItemXSplitV1
    } from '~/types';
    import {
        useContentBlocksUtils
    } from '~/composables';

    const props = defineProps<{
        modelValue: HeroCarouselItemXSplitV1['values'];
        isMobile: boolean;
        breakpoint: string;
        breakpointName: string;
    }>();

    const FullImage = defineAsyncComponent(() => import('~/components/media/FullImage/FullImage.vue'));
    const BlockTypeVideoStatic = defineAsyncComponent(() => import('~/components/content/blocks/v2/BlockTypeVideoStatic/BlockTypeVideoStatic.vue'));
    const BtnUniversal = defineAsyncComponent(() => import('~/components/BtnUniversal/BtnUniversal.vue'));

    const NuxtLink = resolveComponent('NuxtLink');

    const isMobile = computed(() => props.isMobile);
    const { getHeroCarouselItemBindings } = useContentBlocksUtils();

    const {
        buttons,
        isBlockLink,
        rootComponent,
        rootProps,
        textPositionClasses,
        textAlignClass,
        textThemeClass,
        textStrokeClass,
        buttonTheme,
        textOverlayClass,
        textPadClass,
        mediaPadClass,
        mediaPositionClasses
    } = getHeroCarouselItemBindings(() => props.modelValue, () => isMobile.value, NuxtLink);

    const isHeadingImageError = ref<boolean>(false);

    function onHeadingImageError(): void {
        isHeadingImageError.value = true;
    }

    const mediaStyle = computed(() => {
        const sizing = isMobile.value
            ? { width: 'auto', height: '94%' }
            : { width: 'auto', 'max-height': '70vh' };
        return {
            ...sizing,
            'object-position': props.modelValue.imagePosition || 'unset'
        };
    });
</script>

<style lang="scss"></style>
